import { defineComponent, h, useAttrs } from 'vue'
import { VIcon } from 'vuetify/components'

const legacyIconSizes = {
  'x-small': 18,
  small: 16,
  default: 24,
  medium: 28,
  large: 36,
  'x-large': 40
}

const THEME_COLOR_NAMES = new Set([
  'primary',
  'secondary',
  'tertiary',
  'quaternary',
  'accent',
  'error',
  'background',
  'secondaryHighlight',
  'link',
  'lightHighlightBackground',
  'headerPrimary',
  'headerSecondary'
])

function hasBooleanishAttr(value) {
  return value === '' || value === true || value === 'true'
}

function resolveIconSize(attrs) {
  if (attrs.size != null) return attrs.size
  if (hasBooleanishAttr(attrs['x-small']) || hasBooleanishAttr(attrs.xSmall)) {
    return legacyIconSizes['x-small']
  }
  if (hasBooleanishAttr(attrs.small)) return legacyIconSizes.small
  if (hasBooleanishAttr(attrs.medium)) return legacyIconSizes.medium
  if (hasBooleanishAttr(attrs.large)) return legacyIconSizes.large
  if (hasBooleanishAttr(attrs['x-large']) || hasBooleanishAttr(attrs.xLarge)) {
    return legacyIconSizes['x-large']
  }
  return undefined
}

function normalizeIconAttrs(attrs) {
  const normalized = { ...attrs }
  delete normalized.small
  delete normalized.medium
  delete normalized.large
  delete normalized['x-small']
  delete normalized.xSmall
  delete normalized['x-large']
  delete normalized.xLarge
  delete normalized.left
  delete normalized.right
  return normalized
}

function resolveSlotText(slots) {
  const children = slots.default?.()
  const firstChild = children?.find((child) => child?.children != null)
  return typeof firstChild?.children === 'string'
    ? firstChild.children.trim()
    : ''
}

function resolveColorStyle(color) {
  if (!color || typeof color !== 'string') return undefined
  if (
    color.startsWith('#') ||
    color.startsWith('rgb') ||
    color.startsWith('var(')
  ) {
    return color
  }

  return THEME_COLOR_NAMES.has(color) ? `rgb(var(--v-theme-${color}))` : color
}

function normalizeStyle(style, attrs) {
  const resolvedStyle = Array.isArray(style) ? [...style] : [style]
  const color = resolveColorStyle(attrs.color)

  if (color) {
    resolvedStyle.push({ color })
  }

  return resolvedStyle.filter(Boolean)
}

export default defineComponent({
  name: 'VIconCompat',
  compatConfig: {
    RENDER_FUNCTION: false,
    COMPONENT_V_MODEL: false,
    INSTANCE_ATTRS_CLASS_STYLE: false
  },
  inheritAttrs: false,

  setup(_, { slots }) {
    const attrs = useAttrs()

    return () => {
      const normalizedAttrs = normalizeIconAttrs(attrs)
      const size = resolveIconSize(attrs)
      const iconName = resolveSlotText(slots)
      const isLegacyMdiIcon = iconName.startsWith('mdi-')
      const legacyClasses = {
        'v-icon--left': hasBooleanishAttr(attrs.left),
        'v-icon--right': hasBooleanishAttr(attrs.right),
        'v-icon--legacy-left': hasBooleanishAttr(attrs.left),
        'v-icon--legacy-right': hasBooleanishAttr(attrs.right)
      }

      if (isLegacyMdiIcon) {
        return h('i', {
          ...normalizedAttrs,
          'aria-hidden': normalizedAttrs['aria-hidden'] ?? 'true',
          class: [
            'v-icon',
            'notranslate',
            'mdi',
            iconName,
            'theme--light',
            normalizedAttrs.class,
            legacyClasses
          ],
          role: normalizedAttrs.role ?? 'img',
          style: [
            ...normalizeStyle(normalizedAttrs.style, attrs),
            size
              ? { fontSize: typeof size === 'number' ? `${size}px` : size }
              : null
          ]
        })
      }

      return h(
        VIcon,
        {
          ...normalizedAttrs,
          ...(size ? { size } : {}),
          class: [normalizedAttrs.class, legacyClasses]
        },
        slots
      )
    }
  }
})
